<script setup lang="ts">
  import {
    useElementSize,
    useIntervalFn,
    usePreferredReducedMotion,
    useScroll,
    useTimeoutFn,
  } from '@vueuse/core'
  import { computed, useTemplateRef, watch } from 'vue'

  const totalSlides = 4
  const carouselRef = useTemplateRef('carouselRef')
  const videoRef = useTemplateRef('videoRef')
  const { width } = useElementSize(carouselRef)
  const reducedMotion = usePreferredReducedMotion()
  const { x } = useScroll(carouselRef, { behavior: 'smooth' })
  const { pause: stopAutoScroll, resume: startAutoScroll } = useIntervalFn(() => nextSlide(), 8000)
  const { start: startResumeTimer, stop: stopResumeTimer } = useTimeoutFn(
    () => startAutoScroll(),
    20000,
    { immediate: false },
  )

  const activeIndex = computed(() => {
    if (!width.value) return 0

    return Math.round(x.value / width.value)
  })

  const scrollToIndex = (index: number) => {
    if (width.value) x.value = index * width.value
  }

  const nextSlide = () => {
    const nextIndex = (activeIndex.value + 1) % totalSlides
    scrollToIndex(nextIndex)
  }

  const manualSwitch = (index: number) => {
    stopAutoScroll()
    stopResumeTimer()
    scrollToIndex(index)
    startResumeTimer()
  }

  watch(activeIndex, (newIndex) => {
    if (reducedMotion.value === 'reduce') return
    if (newIndex === 3) {
      videoRef.value?.play()
    } else {
      videoRef.value?.pause()
    }
  })
</script>

<template>
  <div class="relative w-full border-b border-black/10 text-white">
    <div
      ref="carouselRef"
      class="scrollbar-hide flex h-75 w-full snap-x snap-mandatory overflow-x-auto scroll-smooth"
    >
      <div class="absolute inset-0 z-9 bg-[#080517]/75"></div>
      <!-- Slide 1 -->
      <div
        class="flex h-full w-full flex-none snap-center items-center justify-center bg-[url('/images/carousel/Hero1.png')] bg-cover bg-center p-4"
      >
        <div class="relative z-10 mx-auto max-w-4xl text-center">
          <h1 class="font-display text-tan-100 mb-4 text-5xl font-bold drop-shadow-md md:text-6xl">
            Your #1 Stop for
            <span class="text-yellow-400">Turnips</span>
          </h1>
          <p class="text-tan-300 text-md font-medium tracking-wide">
            Now built for
            <strong class="text-white"> Animal Crossing: New Horizons 3.0 </strong>
          </p>
        </div>
      </div>

      <!-- Slide 2 -->
      <div
        class="flex h-full w-full flex-none snap-center items-center justify-center bg-[url('/images/carousel/Hero2.png')] bg-cover bg-center p-4"
      >
        <div class="relative z-10 mx-auto max-w-4xl text-center">
          <h1 class="font-display text-tan-100 mb-4 text-5xl font-bold drop-shadow-md md:text-6xl">
            Support Us On
            <span class="text-teal-300">Patreon</span>
          </h1>
          <p class="text-tan-300 text-md font-medium tracking-wide">
            We couldn't do this <strong class="text-white"> without you all! </strong>
          </p>
          <div class="mt-4 flex justify-center">
            <a
              href="https://patreon.warp.world/"
              target="_blank"
              class="bg-base-green flex cursor-pointer items-center rounded-lg px-3 py-3 font-bold text-[#080517] shadow-lg shadow-blue-500/20 transition-all hover:text-white"
            >
              Subscribe on Patreon
            </a>
          </div>
        </div>
      </div>

      <!-- Slide 3 -->
      <div
        class="flex h-full w-full flex-none snap-center items-center justify-center bg-[url('/images/carousel/Hero3.png')] bg-cover bg-center p-4"
      >
        <div class="relative z-10 mx-auto max-w-4xl text-center">
          <h1 class="font-display text-tan-100 mb-4 text-5xl font-bold drop-shadow-md md:text-6xl">
            Join Us On
            <span class="text-blue-400">Discord</span>
          </h1>
          <p class="text-tan-300 text-md font-medium tracking-wide">
            Make Friends, Discuss the Stalk Market, and More!
          </p>
          <div class="mt-4 flex justify-center">
            <a
              href="https://discord.gg/rKCtJwDSry"
              target="_blank"
              class="flex cursor-pointer items-center rounded-lg bg-blue-400 px-3 py-3 font-bold text-[#080517] shadow-lg shadow-blue-500/20 transition-all hover:text-white"
            >
              Join Discord
            </a>
          </div>
        </div>
      </div>

      <!-- Slide 4 -->
      <div
        class="relative flex h-full w-full flex-none snap-center items-center justify-center bg-[url('/images/carousel/Hero3.png')] bg-cover bg-center p-4"
      >
        <video
          ref="videoRef"
          class="absolute inset-0 z-8 h-full w-full object-cover"
          src="/images/carousel/Hero4.mp4"
          muted
          loop
          playsinline
        />
        <div class="relative z-10 mx-auto max-w-4xl text-center">
          <h1 class="font-display text-tan-100 mb-4 text-5xl font-bold drop-shadow-md md:text-6xl">
            Check out
            <span class="text-base-orange">Crowd Control</span>
          </h1>
          <p class="text-tan-300 text-md font-medium tracking-wide">
            Make your Animal Crossing streams interactive with on-screen Jumpscapes, Minigames and
            more!
          </p>
          <div class="mt-4 flex justify-center">
            <a
              href="https://crowdcontrol.live"
              target="_blank"
              class="bg-base-orange flex cursor-pointer items-center rounded-lg px-3 py-3 font-bold text-[#080517] shadow-lg shadow-blue-500/20 transition-all hover:text-white"
            >
              Stream the Interactive Way
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Gradient Overlay -->
    <div
      class="pointer-events-none absolute bottom-0 h-1/2 w-full bg-linear-to-t from-white/5 to-transparent"
    ></div>

    <!-- Navigation Dots -->
    <div class="absolute right-0 bottom-8 left-0 z-20 flex justify-center gap-3">
      <button
        v-for="i in totalSlides"
        :key="i"
        @click="manualSwitch(i - 1)"
        class="h-3 w-3 cursor-pointer rounded-full shadow-sm transition-all duration-300 focus:outline-none"
        :class="
          activeIndex === i - 1
            ? 'scale-125 bg-white opacity-100'
            : 'bg-white opacity-40 hover:opacity-75'
        "
        :aria-label="`Go to slide ${i}`"
      />
    </div>
  </div>
</template>
